import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import { LanguageContext } from '@lib/language-context'
import { ProductCombinedListingContext } from '@lib/product/combined-listing/context'
import { getIsEmptyFrame } from '@lib/product/combined-listing/helpers'
import { useCombinedListingProductPrice } from '@lib/product/combined-listing/price'
import { getCombinedListingProductWithCache } from '@lib/product/combined-listing/request'
import { colorOptionNames } from '@lib/product/product'
import { StringsContext } from '@lib/strings-context'
import { UserContext } from '@lib/user/context'
import { getHasUserPurchasePermission } from '@lib/user/helpers'

import CombinedListingProductActions from '@blocks/product/combined-listing-product-actions'
import CombinedListingProductSelect from '@blocks/product/combined-listing-product-select'
import ProductPrice from '@blocks/product/product-price'

interface QuickAddModalCombinedListingProductControlsProps {
  product: SanityProductFragment
}

const QuickAddModalCombinedListingProductControls = ({
  product,
}: QuickAddModalCombinedListingProductControlsProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)
  const {
    combinedListingConfiguration,
    combinedListingArtworkSize,
    combinedListingProducts,
    combinedListingVariants,
    combinedListingFrameSize,
    setCombinedListingSettings,
    setCombinedListingConfiguration,
    setCombinedListingMainProductId,
    setCombinedListingStateManagerType,
    clearCombinedListing,
  } = useContext(ProductCombinedListingContext)
  const { user } = useContext(UserContext)

  const [isError, setIsError] = useState(false)

  const configurationLoadedRef = useRef(false)

  const frameColor = useMemo(() => {
    const colorOption = product?.options?.find((option) =>
      colorOptionNames.includes(option.name),
    )

    return colorOption?.values?.[0]
  }, [product])

  const combinedListingProductIds = useMemo(
    () =>
      combinedListingProducts?.map(
        (combinedListingActiveProduct) =>
          combinedListingActiveProduct.productID,
      ) ?? [],
    [combinedListingProducts],
  )

  const combinedListingVariantIds = useMemo(
    () =>
      combinedListingVariants?.map(
        (combinedListingActiveVariant) =>
          combinedListingActiveVariant.variantID,
      ) ?? [],
    [combinedListingVariants],
  )

  const isEmptyFrame = useMemo(() => {
    return getIsEmptyFrame(combinedListingProducts ?? [])
  }, [combinedListingProducts])

  const [price, comparePrice] = useCombinedListingProductPrice(
    combinedListingProducts,
    combinedListingVariants,
    combinedListingConfiguration,
  )

  // Load configuration when opening modal
  useEffect(() => {
    if (product && !configurationLoadedRef.current) {
      configurationLoadedRef.current = true

      const loadCombinedListingProduct = async () => {
        try {
          const newProduct = await getCombinedListingProductWithCache(
            locale,
            product.productID,
          )

          setIsError(false)

          // Load configuration
          setCombinedListingStateManagerType('memory')
          setCombinedListingMainProductId(product.productID)
          setCombinedListingConfiguration(newProduct.productCombinedListing)
          setCombinedListingSettings(newProduct.productCombinedListingSettings)

          // Clear combined listing selection
          clearCombinedListing()
        } catch (error) {
          console.log(error)

          setIsError(true)
        }
      }
      loadCombinedListingProduct()
    }

    return () => {
      // Unload configuration
      setCombinedListingConfiguration()
      setCombinedListingSettings()
      configurationLoadedRef.current = false

      // Clear combined listing selection
      clearCombinedListing()
    }
  }, [
    clearCombinedListing,
    locale,
    product,
    setCombinedListingConfiguration,
    setCombinedListingMainProductId,
    setCombinedListingSettings,
    setCombinedListingStateManagerType,
  ])

  if (!product) {
    return null
  }

  const hasUserPurchasePermission = getHasUserPurchasePermission(user)
  const hasActions = !!user && hasUserPurchasePermission

  return (
    <>
      {isError && <div className="p-5">{strings.combinedListingErrorText}</div>}

      {!combinedListingConfiguration && !isError && (
        <div className="p-5">{strings.combinedListingLoadingText}</div>
      )}

      {!!combinedListingConfiguration && !isError && (
        <>
          <div className="overflow-y-scroll no-scrollbar px-5 pb-5">
            {hasUserPurchasePermission && (
              <ProductPrice
                size="lg"
                price={price}
                comparePrice={comparePrice}
                showPrice
                showPriceTaxSuffix
                showCurrency
              />
            )}

            <CombinedListingProductSelect
              onMainProductChange={(product) =>
                setCombinedListingMainProductId(product.productID)
              }
              frameColor={frameColor}
            />
          </div>

          {hasActions && (
            <div className="p-5">
              <CombinedListingProductActions
                product={product}
                productIds={combinedListingProductIds}
                variantIds={combinedListingVariantIds}
                frameSize={combinedListingFrameSize ?? ''}
                artworkSize={combinedListingArtworkSize ?? undefined}
                isEmptyFrame={isEmptyFrame}
              />
            </div>
          )}
        </>
      )}
    </>
  )
}

export default QuickAddModalCombinedListingProductControls
